import { VersionHelper } from './version.helper';

/** The entities a nested v3 path can address, in the order they appear in the URL. */
export type NestedEntityType = 'program' | 'collection' | 'course' | 'indicator' | 'card' | 'event' | 'assessment' | 'assignment';

export interface NestedPathSegment {
  type: NestedEntityType;
  uri: string;
}

export interface ParsedNestedPath {
  segments: NestedPathSegment[];
  currentEntity: NestedPathSegment | null;
  parentPath: string;
  fullPath: string;
  depth: number;
}

export class NestedPathHelper {
  /**
   * Rewrites a legacy (v2) backend URI to its v3 route when the v3 UI is active.
   * Only exact playlist / playlist+card URIs are mapped; deeper v2-only paths
   * (e.g. assessments) are left untouched so they still resolve in the v2 views.
   */
  static mapLegacyUriToV3(uri: string): string {
    if (!uri || !VersionHelper.isV3Enabled()) {
      return uri;
    }
    const cleaned = uri.replace(/\/page\/\d+(?=[/?#]|$)/, '');
    const match = cleaned.match(/^\/playlists\/([^/?#]+)(\/cards\/[^/?#]+)?([?#].*)?$/);
    if (!match) {
      return uri;
    }
    return `/programs/${match[1]}${match[2] ?? ''}${match[3] ?? ''}`;
  }

  /** URL keywords that introduce an entity uri, in both the v3 nested routes and the v2 ones. */
  private static readonly URI_COLLECTIONS = [
    'programs',
    'collections',
    'courses',
    'indicators',
    'cards',
    'events',
    'assessments',
    'assignments',
    'playlists',
    'pages',
  ];

  /**
   * Swaps one entity's uri inside a URL, matching on path segments rather than on the raw
   * string: a uri that also appears as a parent segment, a tab name or a query value stays
   * untouched. The deepest match wins, which is the entity an editor is addressing.
   *
   * Pass `collection` when the caller knows the keyword (`cards`, `collections`, …) to keep
   * a card and its same-named parent apart.
   */
  static replaceEntityUri(url: string, oldUri: string, newUri: string, collection?: string): string {
    if (!url || !oldUri || !newUri || oldUri === newUri) {
      return url;
    }

    const suffixIndex = url.search(/[?#]/);
    const path = suffixIndex === -1 ? url : url.substring(0, suffixIndex);
    const suffix = suffixIndex === -1 ? '' : url.substring(suffixIndex);
    const segments = path.split('/');

    let matchIndex = -1;
    for (let i = 0; i < segments.length - 1; i++) {
      const isKnownCollection = collection ? segments[i] === collection : this.URI_COLLECTIONS.includes(segments[i]);
      if (isKnownCollection && segments[i + 1] === oldUri) {
        matchIndex = i + 1;
      }
    }

    if (matchIndex === -1) {
      return url;
    }

    segments[matchIndex] = newUri;
    return segments.join('/') + suffix;
  }

  static resolveCardSlug(card: { uri?: string; formattedUri?: string }): string | undefined {
    if (card?.uri) {
      return card.uri;
    }
    const fromFormatted = card?.formattedUri
      ?.split('?')[0]
      .split('/')
      .filter((segment) => segment.length > 0)
      .pop();
    return fromFormatted || undefined;
  }

  static parsePath(url: string): ParsedNestedPath | null {
    if (!url) {
      return null;
    }

    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    const [mainPath] = cleanUrl.split('?');

    const pathSegments = mainPath.split('/').filter((segment) => segment.length > 0);

    if (pathSegments.length < 2) {
      return null;
    }

    const segments: NestedPathSegment[] = [];
    let currentEntity: NestedPathSegment | null = null;
    let i = 0;
    const tabKeywords = ['edit', 'content', 'collaborators', 'analytics', 'members', 'publish-settings'];

    while (i < pathSegments.length) {
      const segment = pathSegments[i];

      if (segment === 'programs' && i + 1 < pathSegments.length) {
        const programUri = pathSegments[i + 1];
        segments.push({ type: 'program', uri: programUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'program', uri: programUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'programs') {
          continue;
        } else if (nextSegment === 'collections') {
          continue;
        } else if (nextSegment === 'courses') {
          continue;
        } else if (nextSegment === 'cards') {
          currentEntity = { type: 'program', uri: programUri };
          continue;
        } else if (nextSegment === 'events') {
          currentEntity = { type: 'program', uri: programUri };
          continue;
        } else if (nextSegment === 'assessments') {
          currentEntity = { type: 'program', uri: programUri };
          continue;
        } else if (nextSegment === 'indicators') {
          continue;
        } else if (tabKeywords.includes(nextSegment)) {
          currentEntity = { type: 'program', uri: programUri };
          break;
        } else {
          break;
        }
      } else if (segment === 'collections' && i + 1 < pathSegments.length) {
        const collectionUri = pathSegments[i + 1];
        segments.push({ type: 'collection', uri: collectionUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'collection', uri: collectionUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'programs') {
          continue;
        } else if (nextSegment === 'collections') {
          continue;
        } else if (nextSegment === 'courses') {
          continue;
        } else if (nextSegment === 'cards') {
          currentEntity = { type: 'collection', uri: collectionUri };
          continue;
        } else if (nextSegment === 'events') {
          currentEntity = { type: 'collection', uri: collectionUri };
          continue;
        } else if (nextSegment === 'assessments') {
          currentEntity = { type: 'collection', uri: collectionUri };
          continue;
        } else if (nextSegment === 'indicators') {
          continue;
        } else if (tabKeywords.includes(nextSegment)) {
          currentEntity = { type: 'collection', uri: collectionUri };
          break;
        } else {
          break;
        }
      } else if (segment === 'courses' && i + 1 < pathSegments.length) {
        const courseUri = pathSegments[i + 1];
        segments.push({ type: 'course', uri: courseUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'course', uri: courseUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'programs') {
          break;
        } else if (nextSegment === 'cards') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'events') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'assessments') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'assignments') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'courses') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'collections') {
          continue;
        } else if (nextSegment === 'indicators') {
          continue;
        } else if (tabKeywords.includes(nextSegment)) {
          currentEntity = { type: 'course', uri: courseUri };
          break;
        } else {
          break;
        }
      } else if (segment === 'indicators' && i + 1 < pathSegments.length) {
        const indicatorUri = pathSegments[i + 1];
        segments.push({ type: 'indicator', uri: indicatorUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'indicator', uri: indicatorUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'indicators') {
          continue;
        }
        currentEntity = { type: 'indicator', uri: indicatorUri };
        break;
      } else if (segment === 'cards' && i + 1 < pathSegments.length) {
        const cardUri = pathSegments[i + 1];
        segments.push({ type: 'card', uri: cardUri });
        currentEntity = { type: 'card', uri: cardUri };
        i += 2;
        break;
      } else if (segment === 'events' && i + 1 < pathSegments.length) {
        const eventUri = pathSegments[i + 1];
        segments.push({ type: 'event', uri: eventUri });
        currentEntity = { type: 'event', uri: eventUri };
        i += 2;
        break;
      } else if (segment === 'assessments' && i + 1 < pathSegments.length) {
        const assessmentUri = pathSegments[i + 1];
        segments.push({ type: 'assessment', uri: assessmentUri });
        currentEntity = { type: 'assessment', uri: assessmentUri };
        i += 2;
        break;
      } else if (segment === 'assignments' && i + 1 < pathSegments.length) {
        const assignmentUri = pathSegments[i + 1];
        segments.push({ type: 'assignment', uri: assignmentUri });
        currentEntity = { type: 'assignment', uri: assignmentUri };
        i += 2;
        break;
      } else if (tabKeywords.includes(segment)) {
        break;
      } else {
        break;
      }
    }

    if (segments.length === 0) {
      return null;
    }

    const fullPath = this.buildFullPath(segments);
    const parentPath = segments.length > 1 ? this.buildFullPath(segments.slice(0, -1)) : '';

    return {
      segments,
      currentEntity: currentEntity || segments[segments.length - 1],
      parentPath,
      fullPath,
      depth: segments.length,
    };
  }

  static buildFullPath(segments: NestedPathSegment[]): string {
    if (segments.length === 0) {
      return '';
    }

    const pathParts: string[] = [];
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;

      if (segment.type === 'program') {
        pathParts.push('programs');
        pathParts.push(segment.uri);
      } else if (segment.type === 'collection') {
        pathParts.push('collections');
        pathParts.push(segment.uri);
      } else if (segment.type === 'course') {
        pathParts.push('courses');
        pathParts.push(segment.uri);
      } else if (segment.type === 'indicator') {
        pathParts.push('indicators');
        pathParts.push(segment.uri);
      } else if (segment.type === 'card') {
        pathParts.push('cards');
        pathParts.push(segment.uri);
      } else if (segment.type === 'event') {
        pathParts.push('events');
        pathParts.push(segment.uri);
      } else if (segment.type === 'assessment') {
        pathParts.push('assessments');
        pathParts.push(segment.uri);
      } else if (segment.type === 'assignment') {
        pathParts.push('assignments');
        pathParts.push(segment.uri);
      }
    }

    return '/' + pathParts.join('/');
  }

  static buildPathForEntity(entityType: NestedEntityType, entityUri: string, parentSegments: NestedPathSegment[] = []): string {
    const newSegment: NestedPathSegment = { type: entityType, uri: entityUri };
    const allSegments = [...parentSegments, newSegment];
    return this.buildFullPath(allSegments);
  }

  static getParentPath(url: string): string {
    const parsed = this.parsePath(url);
    return parsed?.parentPath || '';
  }

  /**
   * The parent path to load an event with, so it comes back with its playlist context. The backend's `in=` parser
   * only knows programmes, courses and collections, so an event under an indicator is loaded on its own.
   */
  static getEventPlaylistPath(url: string): string | undefined {
    const parentPath = this.getParentPath(url);
    return parentPath && !parentPath.includes('/indicators/') ? parentPath : undefined;
  }

  static getCurrentEntity(url: string): NestedPathSegment | null {
    const parsed = this.parsePath(url);
    return parsed?.currentEntity || null;
  }

  static getAllSegments(url: string): NestedPathSegment[] {
    const parsed = this.parsePath(url);
    return parsed?.segments || [];
  }

  static getParentSegments(url: string): NestedPathSegment[] {
    const parsed = this.parsePath(url);
    if (!parsed || parsed.segments.length <= 1) {
      return [];
    }
    return parsed.segments.slice(0, -1);
  }

  static getOneLevelUpPath(url: string, tab: string = 'content'): string {
    const parsed = this.parsePath(url);
    if (!parsed || parsed.segments.length <= 1) {
      if (parsed?.currentEntity?.type === 'event') {
        return '/upcoming';
      }
      return parsed?.currentEntity?.type === 'collection' ? '/programs?filter=collections' : '/programs';
    }

    const parentSegments = parsed.segments.slice(0, -1);
    const parentPath = this.buildFullPath(parentSegments);

    return `${parentPath}/${tab}`;
  }

  static addEntityToPath(currentPath: string, entityType: NestedEntityType, entityUri: string, tab: string = 'content'): string {
    const parsed = this.parsePath(currentPath);
    const parentSegments = parsed ? parsed.segments : [];
    const newPath = this.buildPathForEntity(entityType, entityUri, parentSegments);
    return `${newPath}/${tab}`;
  }

  static getProgramUri(url: string, level: number = 0): string | null {
    const segments = this.getAllSegments(url);
    const programSegments = segments.filter((s) => s.type === 'program');
    if (level >= 0 && level < programSegments.length) {
      return programSegments[programSegments.length - 1 - level]?.uri || null;
    }
    return null;
  }

  static getCourseUri(url: string, level: number = 0): string | null {
    const segments = this.getAllSegments(url);
    const courseSegments = segments.filter((s) => s.type === 'course');
    if (level >= 0 && level < courseSegments.length) {
      return courseSegments[courseSegments.length - 1 - level]?.uri || null;
    }
    return null;
  }

  static getCurrentProgramUri(url: string): string | null {
    const parsed = this.parsePath(url);
    if (parsed?.currentEntity?.type === 'program') {
      return parsed.currentEntity.uri;
    }
    return this.getProgramUri(url, 0);
  }

  static getCurrentCourseUri(url: string): string | null {
    const parsed = this.parsePath(url);
    if (parsed?.currentEntity?.type === 'course') {
      return parsed.currentEntity.uri;
    }
    return this.getCourseUri(url, 0);
  }

  static getBackUrl(currentUrl: string): string {
    const cleanUrl = currentUrl.split('?')[0];
    const segments = this.getAllSegments(cleanUrl);

    if (segments.length === 0) {
      return '/';
    }

    if (segments.length === 1) {
      const firstSegment = segments[0];
      if (firstSegment.type === 'collection') {
        return '/programs?filter=collections';
      } else if (firstSegment.type === 'program' || firstSegment.type === 'course') {
        return '/programs';
      } else if (firstSegment.type === 'event') {
        return '/upcoming';
      }
      return '/';
    }

    const parentSegments = segments.slice(0, -1);
    const parentPath = this.buildFullPath(parentSegments);
    return `${parentPath}/content`;
  }

  /**
   * Get the back URL for edit pages.
   * From edit page, back goes to the same entity's content page, not to parent list.
   * Example: /programs/xyz/edit -> /programs/xyz/content
   */
  static getBackUrlFromEdit(currentUrl: string): string {
    const cleanUrl = currentUrl.split('?')[0];

    // If URL ends with /edit, replace with /content
    if (cleanUrl.endsWith('/edit')) {
      return cleanUrl.replace(/\/edit$/, '/content');
    }

    // For other edit-related pages (collaborators, analytics, etc.), go to content
    const editTabs = [
      '/collaborators',
      '/analytics',
      '/participants',
      '/publish-settings',
      '/completion-logic',
      '/badges',
      '/languages',
      '/edit-members',
      '/edit-tasks',
      '/edit-skills',
    ];
    for (const tab of editTabs) {
      if (cleanUrl.endsWith(tab)) {
        return cleanUrl.replace(new RegExp(`${tab}$`), '/content');
      }
    }

    // Fallback to regular getBackUrl
    return this.getBackUrl(currentUrl);
  }

  /**
   * Validate that a returnUrl (typically from a query param) is a safe,
   * same-origin relative path before it is passed to router.navigateByUrl().
   */
  static sanitizeReturnUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    const trimmed = url.trim();

    // Must be an absolute path on this origin.
    if (!trimmed.startsWith('/')) {
      return null;
    }

    // Reject protocol-relative URLs (//host) and backslash bypasses (/\host).
    if (trimmed.startsWith('//') || trimmed.startsWith('/\\')) {
      return null;
    }

    // Reject anything carrying a scheme or backslashes anywhere.
    if (trimmed.includes('\\') || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
      return null;
    }

    return trimmed;
  }

  // Edit/manage pages a read view must never treat as its "back" target — otherwise
  // opening a read view from an editor would loop straight back into the editor.
  private static readonly EDIT_URL_SEGMENTS = [
    'edit',
    'edit-skills',
    'edit-tasks',
    'edit-members',
    'collaborators',
    'analytics',
    'participants',
    'publish-settings',
    'completion-logic',
    'badges',
  ];

  static isEditUrl(url: string | null | undefined): boolean {
    if (!url) {
      return false;
    }
    const clean = url.split('?')[0].replace(/\/$/, '');
    // Admin keeps its own back semantics; only guard the learner/manage entity pages.
    if (clean.startsWith('/admin')) {
      return false;
    }
    const lastSegment = clean.substring(clean.lastIndexOf('/') + 1);
    return this.EDIT_URL_SEGMENTS.includes(lastSegment) || /(^|\/)edit(\/|$)/.test(clean);
  }

  static sanitizeBackTo(url: string | null | undefined): string | null {
    const safe = this.sanitizeReturnUrl(url);
    if (!safe || this.isEditUrl(safe)) {
      return null;
    }
    return safe;
  }

  // Key identifying the entity/context a URL belongs to, so different tabs of the
  // same entity collapse to one history entry (and a Back target is never "self").
  static contextKey(url: string): string {
    const clean = (url || '').split('?')[0];
    const segments = this.getAllSegments(clean);
    return segments.length ? this.buildFullPath(segments) : clean;
  }

  // A navigation-history entry is only a valid Back target if it is safe, not an edit
  // page and not the entity we are already viewing.
  static sanitizeHistoryTarget(target: string | null | undefined, currentUrl: string): string | null {
    const safe = this.sanitizeBackTo(target);
    if (!safe || this.contextKey(safe) === this.contextKey(currentUrl)) {
      return null;
    }
    return safe;
  }

  // Back target: an explicit ?backTo wins (survives refresh/share), else the route the
  // user actually came from (navigation history, which retraces the real path without
  // ping-ponging), else the natural parent. All guarded so a read view never lands on
  // an edit page or on itself.
  static resolveBackTarget(currentUrl: string, backToParam: string | null | undefined, historyTarget: string | null | undefined): string {
    const backTo = this.sanitizeBackTo(backToParam);
    if (backTo) {
      return backTo;
    }
    const history = this.sanitizeHistoryTarget(historyTarget, currentUrl);
    if (history) {
      return history;
    }
    return this.getBackUrl(currentUrl);
  }

  // Contextual label for the Back link based on where it points.
  static resolveBackLabelKey(backUrl: string): string {
    const path = (backUrl || '').split('?')[0];
    if (path === '/') {
      return 'translations.global.button.backToMyHub';
    }
    if (path === '/programs') {
      return 'translations.programs.backToPrograms';
    }
    if (path === '/upcoming' || path.startsWith('/upcoming/')) {
      return 'translations.global.button.backToCalendar';
    }
    return 'translations.global.button.back';
  }
}
